import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CompareResult } from 'src/app/compare-result';
import { Registration } from 'src/app/models/registration';
import { SoldPlate } from 'src/app/models/soldPlate';
import { CompareService } from 'src/app/services/compare-service';
import { QuickSignupService } from 'src/app/services/quick-signup-service';
import { RegistrationService } from 'src/app/services/registration-service';
import { SearchService } from 'src/app/services/search-service';
import { UserService } from 'src/app/services/user-service';
import { NumberOneResults } from '../number-one-page/number-one-page.component';
import { CHAPTER3_DATA, CHAPTER3_FINDINGS, CHAPTER3_TREND, Chapter3Datum } from './chapter3-data';
import { NumberOnesStateService } from './number-ones-state.service';
import { TIER1_REFERENCE } from './tier1-reference';
import { TIER2_REFERENCE } from './tier2-reference';
import { isDevOnly } from 'src/app/dev2-gate';
import { TrackPlateService } from 'src/app/services/track-plate.service';

/* =============================================================================
   PlateX — Number Ones · The Register (approved design v23).

   Live data per the Content & Data Contract; register-state evidence follows
   the benchmark-reference philosophy: the Single-Single Register is the
   Tier I benchmark, sold-history + NumberPlateRegistry the Tier II interim
   benchmark — reference datasets with provenance (analysis/number-ones/),
   never presented as unqualified public truth. Live listings always win.
   Chapter III runs on the REAL ONS × verified-sales join (chapter3-data.ts,
   generated from analysis/ons-initials/ per the documented methodology).
   ========================================================================== */

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const VERIFIED_SALES_REGS = ['1F', 'JB1', '1NR', '1O', '1E', 'F1'];
const GENUINE_SALE_TYPES = [0, 3, 6, 7, 10, 100];
const POA_SENTINEL_GBP = 10000000;

export interface RegisterCell {
  letters: string;
  combo: string;
  first: string;
  arr: number;
  state: 'available' | 'held' | 'existing' | 'ghost' | 'lost' | 'no-information';
  price?: number; // GBP; undefined on an available cell = POA
}

interface LetterGroup {
  letter: string;
  avail: number;
  inRegister: number;
  cells: RegisterCell[];
}

interface MatrixRow {
  letter: string;
  cells: RegisterCell[];
}

interface PairCell {
  letter: string;
  a: RegisterCell;
  b: RegisterCell;
}

interface SellerRow {
  name: string;
  rating: string;
  finance: boolean;
  price: number;
  best: boolean;
}

interface SaleRow {
  combo: string;
  source: string;
  price: number;
  date: Date;
}

interface ThreeResult {
  combo: string;
  price?: number; // GBP min across sellers; undefined = POA
  reg: Registration; // formatted row for favourite / plate-viewer components
}

interface IvMark {
  letter: string;
  x: number;
  y: number;
  sparse: boolean;
  title: string;
}

@Component({
  selector: 'pla-number-ones-register',
  templateUrl: './number-ones-register.component.html',
  styleUrls: ['./number-ones-register.component.scss'],
})
export class NumberOnesRegisterComponent implements OnInit, AfterViewInit, OnDestroy {
  public letters = LETTERS;

  /* ---- register state (approved defaults: Catalogue · 1 — · All) ---- */
  public view: 'catalogue' | 'matrix' = 'catalogue';
  public arr: 1 | 2 = 1;
  public avail: 'all' | 'available' | 'held' = 'all';

  /* ---- live inventory (canonical plato-search process=number-1) ---- */
  public loading = true;
  public loadFailed = false;
  private twoByArr: { [arr: number]: Map<string, RegisterCell> } = { 1: new Map(), 2: new Map() };
  private singleByArr: { [arr: number]: Map<string, RegisterCell> } = { 1: new Map(), 2: new Map() };
  private threeByKey: Map<string, ThreeResult> = new Map(); // key: arr + letters

  /* ---- view models ---- */
  /* Catalogue shows ONE first letter at a time (26 pairings), picked from the
     A–Z bar, instead of all 676 pairings stacked down the page. Same idea as
     the A–Z pills on the current-plates (new release) page. */
  public catLetter = 'A';
  public catGroups: LetterGroup[] = []; // every letter with cells under the Show filter
  public catCounts: { [letter: string]: number } = {};
  public matrixRows: MatrixRow[] = [];
  public availCount: number | null = null;
  public heldCount: number | null = null; // evidence-backed existing/held cells
  public pinnaclePairs: PairCell[] = [];
  public pinnacleAvail: number | null = null;
  public pinnacleExist: number | null = null;
  public heroAvail: string = '—';
  public threeIssued = '—'; // still MISSING a full issued reference (gap report)
  public threeAvail: string | null = null;
  /* Hero stats are scoped to the TWO-LETTER register (see hero labels), so the
     register high excludes single-letter sales like 1 F. Fallback is the
     last-known verified two-letter high (JB 1, Bonhams Goodwood 2025). */
  public registerHigh = { value: '£608,600', label: 'Two-letter register high · JB 1, 2025', live: false };

  /* ---- Tier III inline search ---- */
  public fieldTerm = '';
  public threeResults: ThreeResult[] | null = null; // null = no search yet

  /* ---- In Focus (editorial pick, Gate 0 clarification 1) ---- */
  public readonly focusReg = { display: '1 NR', plain: '1NR' };
  public focusSellers: SellerRow[] | null = null;
  public focusSellersFailed = false;
  public focusLastSale: { price: number; label: string } | null = null;

  /* ---- Chapter II verified sales ---- */
  public salesRows: SaleRow[] | null = null;

  /* ---- watch form ---- */
  public watchStatus: 'idle' | 'saving' | 'added' | 'error' | 'invalid' = 'idle';
  public watchPair = '';

  /* ---- Chapter III — REAL ONS × verified-sales dataset (generated) ---- */
  public ivData: { [l: string]: Chapter3Datum } = CHAPTER3_DATA;
  public ivFindings = CHAPTER3_FINDINGS;
  public ivTrendR = CHAPTER3_TREND.r;
  public ivSel = 'A';
  public ivInputValue = 'A';
  public ivMarks: IvMark[] = [];
  private byPop: string[] = [];
  private byVal: string[] = [];
  public readonly ivW = 900;
  public readonly ivH = 540;
  private readonly padL = 58;
  private readonly padR = 30;
  private readonly padT = 30;
  private readonly padB = 58;
  public ivGrid = [25, 50, 75];
  public ivTrend = { x1: 0, y1: 0, x2: 0, y2: 0 };
  public ivYTitleY = 0;

  constructor(
    private el: ElementRef<HTMLElement>,
    private searchService: SearchService,
    private compareService: CompareService,
    private registrationService: RegistrationService,
    private userService: UserService,
    private signupService: QuickSignupService,
    private router: Router,
    private stateService: NumberOnesStateService,
    private trackPlate: TrackPlateService
  ) {}

  ngOnInit(): void {
    const snap = this.stateService.takeFresh();
    if (snap) {
      this.arr = snap.arr;
      this.view = snap.view;
      this.avail = snap.avail;
      this.catLetter = snap.catLetter || 'A';
      this.fieldTerm = snap.fieldTerm;
    }
    this.buildIv();
    this.fetchRegister(snap ? snap.scrollY : 0);
    this.fetchFocus();
    this.fetchVerifiedSales();
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.measureShellBound);
    this.stateService.snapshot = {
      arr: this.arr,
      view: this.view,
      avail: this.avail,
      catLetter: this.catLetter,
      fieldTerm: this.fieldTerm,
      scrollY: window.scrollY,
      savedAt: Date.now(),
    };
  }

  ngAfterViewInit(): void {
    // Measure once after layout settles (fonts / announcement bar), and on
    // resize. No scroll tracking needed: the offset depends only on whether the
    // header is fixed (stays) or scrolls away — see measureShell.
    [0, 150, 500, 1200].forEach((t) => setTimeout(() => this.measureShell(), t));
    window.addEventListener('resize', this.measureShellBound, { passive: true });
  }

  private measureShellBound = (): void => this.measureShell();

  private measureShell(): void {
    // Two header regimes on this layout:
    //  - A `fixed` header (legacy shell, local draft / production) stays pinned
    //    over the top, so the toolbar must sit BELOW it (top = header height).
    //  - The dev2 nav-shell is `sticky` but its sticky is broken by an ancestor,
    //    so it SCROLLS AWAY. There is then nothing pinned at the top, so the
    //    toolbar must stick FLUSH to the top (top = 0), never leaving a gap for
    //    register content to show through.
    const shell = (document.querySelector('pla-nav-shell') ||
      document.querySelector('.site-header-container .site-header')) as HTMLElement | null;
    let h = 0;
    if (shell) {
      const pos = getComputedStyle(shell).position;
      h = pos === 'fixed' ? Math.round(shell.getBoundingClientRect().height) : 0;
    }
    this.el.nativeElement.style.setProperty('--shellH', `${h}px`);
    // Expose the real toolbar height so A–Z jumps clear it (it wraps taller on
    // mobile than the desktop 64px).
    const tb = this.el.nativeElement.querySelector('.toolbar') as HTMLElement | null;
    if (tb && tb.offsetHeight > 0) {
      this.el.nativeElement.style.setProperty('--tbH', `${Math.round(tb.offsetHeight)}px`);
    }
  }

  /* =============== plate navigation =============== */
  public plateLink(combo: string): string[] {
    return ['/plate', combo.replace(/\s+/g, '')];
  }
  public openPlate(combo: string, ev?: Event): void {
    if (ev) ev.stopPropagation();
    this.router.navigate(this.plateLink(combo));
  }

  /* =============== live register =============== */
  private fetchRegister(restoreScrollY: number): void {
    this.searchService.getNumber1Canonical((res: NumberOneResults) => {
      if (!res) {
        this.loading = false;
        this.loadFailed = true;
        return;
      }
      this.ingest(res);
      this.loading = false;
      this.renderRegister();
      this.buildPinnacle();
      if (this.fieldTerm) this.runFieldSearch(this.fieldTerm);
      if (restoreScrollY > 0) {
        setTimeout(() => window.scrollTo({ top: restoreScrollY, behavior: 'auto' as ScrollBehavior }), 60);
      }
    });
  }

  private ingest(res: NumberOneResults): void {
    const parse = (reg: string): { letters: string; arr: 1 | 2 } | null => {
      const m1 = /^1\s*([A-Z]{1,3})$/.exec((reg || '').toUpperCase().trim());
      if (m1) return { letters: m1[1], arr: 1 };
      const m2 = /^([A-Z]{1,3})\s*1$/.exec((reg || '').toUpperCase().trim());
      if (m2) return { letters: m2[1], arr: 2 };
      return null;
    };
    const gbpOf = (priceMinor: number): number | undefined => {
      const gbp = priceMinor > 0 ? priceMinor / 100 : 0;
      return gbp > 0 && gbp < POA_SENTINEL_GBP ? gbp : undefined;
    };
    const put = (map: Map<string, RegisterCell>, letters: string, arr: 1 | 2, priceMinor: number) => {
      const price = gbpOf(priceMinor);
      const existing = map.get(letters);
      if (existing) {
        if (price !== undefined && (existing.price === undefined || price < existing.price)) existing.price = price;
        return;
      }
      map.set(letters, {
        letters,
        combo: arr === 1 ? '1 ' + letters : letters + ' 1',
        first: letters[0],
        arr,
        state: 'available',
        price,
      });
    };

    (res.two_by_one || []).forEach((r) => {
      const p = parse(r.registration || '');
      if (p && p.letters.length === 2) put(this.twoByArr[p.arr], p.letters, p.arr, Number(r.price));
    });
    (res.one_by_one || []).forEach((r) => {
      const p = parse(r.registration || '');
      if (p && p.letters.length === 1) put(this.singleByArr[p.arr], p.letters, p.arr, Number(r.price));
    });

    // Three-letter field: formatted rows (favourite/plate-viewer components
    // expect Registration objects), deduped to the min price per combo.
    const formatted = this.registrationService.formatRegistrations(res.three_by_one || []);
    formatted.forEach((r) => {
      const p = parse(r.registration || '');
      if (!p || p.letters.length !== 3) return;
      const combo = p.arr === 1 ? '1 ' + p.letters : p.letters + ' 1';
      if (!r.formattedRegistration) r.formattedRegistration = combo;
      const key = p.arr + p.letters;
      const price = gbpOf(Number(r.price));
      const cur = this.threeByKey.get(key);
      if (cur) {
        if (price !== undefined && (cur.price === undefined || price < cur.price)) cur.price = price;
        return;
      }
      this.threeByKey.set(key, { combo, price, reg: r });
    });

    this.heroAvail = String(this.twoByArr[1].size + this.twoByArr[2].size);
    this.threeAvail = this.threeByKey.size.toLocaleString('en-GB');
  }

  /* Tier II states: live listing → available; otherwise the attributed
     Tier II evidence reference (PlateX sold history = confirmed, or
     NumberPlateRegistry documented = research evidence) → existing;
     otherwise the calm 'no-information'. Benchmark reference, not truth. */
  private cellFor(arr: number, letters: string): RegisterCell {
    const live = this.twoByArr[arr].get(letters);
    if (live) return live;
    const combo = arr === 1 ? '1 ' + letters : letters + ' 1';
    const ref = TIER2_REFERENCE[combo]; // 'held' | 'existing' | undefined
    return {
      letters,
      combo,
      first: letters[0],
      arr,
      state: ref === 'held' ? 'held' : ref === 'existing' ? 'existing' : 'no-information',
    };
  }

  private matchesShow(c: RegisterCell): boolean {
    if (this.avail === 'all') return true;
    if (this.avail === 'available') return c.state === 'available';
    return c.state === 'held' || c.state === 'existing'; // Show → Held
  }

  public renderRegister(): void {
    if (this.loading || this.loadFailed) return;
    const groups: LetterGroup[] = [];
    const counts: { [letter: string]: number } = {};
    let heldTotal = 0;
    LETTERS.forEach((first) => {
      const all = LETTERS.map((second) => this.cellFor(this.arr, first + second));
      const cells = all.filter((c) => this.matchesShow(c));
      const availN = all.filter((c) => c.state === 'available').length;
      const inRegister = all.filter((c) => c.state === 'available' || c.state === 'existing' || c.state === 'held').length;
      heldTotal += inRegister - availN;
      counts[first] = cells.length;
      if (this.avail !== 'all' && cells.length === 0) return;
      groups.push({ letter: first, avail: availN, inRegister, cells });
    });
    this.catGroups = groups;
    this.catCounts = counts;
    // Keep the chosen letter if it still has entries under this filter,
    // otherwise move to the first letter that does.
    if (!counts[this.catLetter] && groups.length) this.catLetter = groups[0].letter;

    this.matrixRows = LETTERS.map((first) => ({
      letter: first,
      cells: LETTERS.map((second) => this.cellFor(this.arr, first + second)),
    }));

    this.availCount = this.twoByArr[this.arr].size;
    this.heldCount = heldTotal;
  }

  private buildPinnacle(): void {
    const single = (arr: 1 | 2, L: string): RegisterCell => {
      const live = this.singleByArr[arr].get(L);
      if (live) return live;
      const combo = arr === 1 ? '1 ' + L : L + ' 1';
      const ref = TIER1_REFERENCE[combo] || 'unconfirmed';
      return {
        letters: L,
        combo,
        first: L,
        arr,
        state: ref === 'never-issued' ? 'ghost' : ref === 'unconfirmed' ? 'no-information' : ref,
      };
    };
    this.pinnaclePairs = LETTERS.map((L) => ({ letter: L, a: single(1, L), b: single(2, L) }));
    this.pinnacleAvail = this.singleByArr[1].size + this.singleByArr[2].size;
    this.pinnacleExist = this.pinnaclePairs.reduce(
      (n, p) =>
        n + [p.a, p.b].filter((c) => c.state === 'available' || c.state === 'held' || c.state === 'existing').length,
      0
    );
  }

  public money(n: number | undefined): string {
    return n === undefined ? 'POA' : '£' + Math.round(n).toLocaleString('en-GB');
  }

  /* =============== In Focus =============== */
  private fetchFocus(): void {
    this.compareService.getPlateComparison(this.focusReg.plain).subscribe(
      (rows: CompareResult[]) => {
        const found = (rows || [])
          .filter((r) => r.status === 'FOUND' && Number(r.price) > 0)
          // Regtransfers is excluded from the public seller comparison
          // (Gate 3 instruction, 18 Jul 2026).
          .filter((r) => !/regtransfers/i.test(((r as any).viewName || '') + ' ' + (r.site || '')))
          .sort((a, b) => Number(a.price) - Number(b.price));
        this.focusSellers = found.map((r, i) => ({
          name: (r as any).viewName || r.site,
          rating: r.trustPilotScore ? String(r.trustPilotScore) : '',
          finance: !!r.finance,
          price: Math.round(Number(r.price)),
          best: i === 0,
        }));
      },
      () => {
        this.focusSellers = [];
        this.focusSellersFailed = true;
      }
    );

    this.registrationService.getPlateHistory(
      this.focusReg.plain,
      (sales: SoldPlate[]) => {
        const genuine = (sales || [])
          .filter((s) => GENUINE_SALE_TYPES.indexOf(Number((s as any).saleType)) > -1)
          .sort((a, b) => new Date(b.sold_date as any).getTime() - new Date(a.sold_date as any).getTime());
        if (genuine.length) {
          const s = genuine[0];
          this.focusLastSale = { price: Number(s.sold_price) / 100, label: 'Last verified sale' };
        }
      },
      () => {}
    );
  }

  /* =============== Chapter II verified sales + A4 =============== */
  private fetchVerifiedSales(): void {
    const collected: SaleRow[] = [];
    let pending = VERIFIED_SALES_REGS.length;
    const done = () => {
      if (--pending > 0) return;
      const byReg = new Map<string, SaleRow>();
      collected.forEach((row) => {
        const cur = byReg.get(row.combo);
        if (!cur || row.date > cur.date) byReg.set(row.combo, row);
      });
      const all = [...byReg.values()].sort((a, b) => b.price - a.price);
      // Chapter II lists verified Number One sales across all tiers.
      this.salesRows = all.slice(0, 6);
      // The hero stat is the TWO-LETTER register high, so single-letter sales
      // (1 F, 1 E, 1 O …) are excluded here — the hero is scoped to Tier II.
      const twoLetterHigh = all.find((r) => r.combo.replace(/[^A-Z]/g, '').length === 2);
      if (twoLetterHigh) {
        this.registerHigh = {
          value: '£' + Math.round(twoLetterHigh.price).toLocaleString('en-GB'),
          label: `Two-letter register high · ${twoLetterHigh.combo}, ${twoLetterHigh.date.getFullYear()}`,
          live: true,
        };
      }
    };
    VERIFIED_SALES_REGS.forEach((reg) => {
      this.registrationService.getPlateHistory(
        reg,
        (sales: SoldPlate[]) => {
          (sales || [])
            .filter((s) => GENUINE_SALE_TYPES.indexOf(Number((s as any).saleType)) > -1)
            .forEach((s) => {
              const d = new Date(s.sold_date as any);
              const combo = this.formatCombo(s.registration || reg);
              const when = isNaN(d.getTime()) ? '' : ' · ' + d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
              collected.push({
                combo,
                source: (s.seller || 'Verified sale') + when,
                price: Number(s.sold_price) / 100,
                date: d,
              });
            });
          done();
        },
        () => done()
      );
    });
  }

  private formatCombo(reg: string): string {
    const m1 = /^1([A-Z]{1,3})$/.exec(reg.toUpperCase());
    if (m1) return '1 ' + m1[1];
    const m2 = /^([A-Z]{1,3})1$/.exec(reg.toUpperCase());
    if (m2) return m2[1] + ' 1';
    return reg;
  }

  /* =============== toolbar handlers =============== */
  public setView(v: 'catalogue' | 'matrix'): void {
    this.view = v;
  }
  public setArr(a: 1 | 2): void {
    this.arr = a;
    this.renderRegister();
  }
  public setAvail(a: 'all' | 'available' | 'held'): void {
    this.avail = a;
    this.renderRegister();
  }
  public get catGroup(): LetterGroup | null {
    return this.catGroups.find((g) => g.letter === this.catLetter) || null;
  }
  /* neighbouring letters with entries, for the prev / next links under the group */
  public catStep(dir: -1 | 1): string | null {
    const i = this.catGroups.findIndex((g) => g.letter === this.catLetter);
    const g = this.catGroups[i + dir];
    return i < 0 || !g ? null : g.letter;
  }
  public setCatLetter(L: string | null, ev?: Event): void {
    if (ev) ev.preventDefault();
    if (!L || !this.catCounts[L]) return;
    const fromFoot = !!ev && (ev.currentTarget as HTMLElement)?.classList?.contains('cat-step');
    this.catLetter = L;
    // From the prev / next links at the foot of a group, bring the new group's
    // head back into view; from the A–Z bar the visitor is already there.
    if (fromFoot) setTimeout(() => this.jumpTo('regAz'), 0);
  }
  public get arrToken(): string {
    return this.arr === 1 ? '1 —' : '— 1';
  }

  /* =============== Tier III inline Number Ones search (F3d) ===============
     Letters only, spaces ignored, uppercase, max three. Wildcards:
       1 letter  L → L??, ?L?, ??L (both arrangements)
       2 letters AB → ?AB, A?B, AB? (both arrangements)
       3 letters ABC → exact only (both arrangements)
     Matched client-side over the live three-letter inventory; deduped. */
  public fieldInput(raw: string): void {
    const cleaned = (raw || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    this.fieldTerm = cleaned;
    this.runFieldSearch(cleaned);
  }

  public runFieldSearch(term: string): void {
    if (!term) {
      this.threeResults = null;
      return;
    }
    const matches3 = (l3: string): boolean => {
      if (term.length === 3) return l3 === term;
      if (term.length === 2) {
        // one wildcard position: deleting one character of l3 yields the term
        for (let i = 0; i < 3; i++) if (l3.slice(0, i) + l3.slice(i + 1) === term) return true;
        return false;
      }
      return l3.indexOf(term) > -1; // single letter, any position
    };
    const out: ThreeResult[] = [];
    this.threeByKey.forEach((res, key) => {
      const l3 = key.slice(1);
      if (matches3(l3)) out.push(res);
    });
    out.sort((a, b) => (b.price || 0) - (a.price || 0));
    this.threeResults = out;
  }

  /* =============== register watch =============== */
  public submitWatch(pairing: string, email: string): void {
    const letters = (pairing || '').toUpperCase().replace(/[^A-Z]/g, '');
    if (letters.length < 1 || letters.length > 3) {
      this.watchStatus = 'invalid';
      return;
    }
    this.watchPair = letters;
    this.watchStatus = 'saving';
    this.signupService.quickSignup(
      `number-ones register watch ${letters}`,
      email || undefined,
      (ok: boolean) => {
        if (!ok) {
          this.watchStatus = 'idle';
          return;
        }
        this.addWatches(letters);
      },
      () => this.addWatches(letters)
    );
    setTimeout(() => {
      if (this.watchStatus === 'saving') this.watchStatus = 'idle';
    }, 30000);
  }

  private watchesAdded = 0;
  private addWatches(letters: string): void {
    if (this.watchStatus === 'added') return;
    this.watchesAdded = 0;
    const targets = ['1' + letters, letters + '1'];
    // dev2: Track plate (followed plates). Idempotent, so the sign-in box
    // calling this twice (callback and complete) cannot untrack, unlike the
    // legacy notify toggle prod still uses.
    if (isDevOnly) {
      targets.forEach((reg) =>
        this.trackPlate.commit(reg).subscribe(() => {
          this.watchesAdded++;
          if (this.watchesAdded >= targets.length) this.watchStatus = 'added';
        })
      );
      return;
    }
    targets.forEach((reg) =>
      this.userService.notifyRegistrationString(reg, () => {
        this.watchesAdded++;
        if (this.watchesAdded >= targets.length) this.watchStatus = 'added';
      })
    );
  }

  /* =============== in-page navigation =============== */
  public jumpTo(id: string, ev?: Event): void {
    if (ev) ev.preventDefault();
    const target = this.el.nativeElement.querySelector('#' + id) as HTMLElement | null;
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* =============== Chapter III scatter (real data) =============== */
  private buildIv(): void {
    this.byPop = LETTERS.slice().sort((a, b) => this.ivData[b].pop - this.ivData[a].pop);
    this.byVal = LETTERS.slice().sort((a, b) => this.ivData[b].val - this.ivData[a].val);
    this.ivMarks = LETTERS.map((l) => {
      const d = this.ivData[l];
      return {
        letter: l,
        x: this.ivX(d.pop),
        y: this.ivY(d.val),
        sparse: d.ev === 'sparse',
        title: `${l} — popularity #${this.rankPop(l)}, value #${this.rankVal(l)}`,
      };
    });
    // computed least-squares trend over the real data (chapter3-data.ts)
    const yAt = (x: number) => Math.max(0, Math.min(100, CHAPTER3_TREND.intercept + CHAPTER3_TREND.slope * x));
    this.ivTrend = { x1: this.ivX(0), y1: this.ivY(yAt(0)), x2: this.ivX(100), y2: this.ivY(yAt(100)) };
    this.ivYTitleY = this.padT + (this.ivH - this.padT - this.padB) / 2;
  }
  private ivX(p: number): number {
    return this.padL + (p / 100) * (this.ivW - this.padL - this.padR);
  }
  private ivY(v: number): number {
    return this.padT + (1 - v / 100) * (this.ivH - this.padT - this.padB);
  }
  public gx(p: number): number {
    return this.ivX(p);
  }
  public gy(v: number): number {
    return this.ivY(v);
  }
  public get ivPlot() {
    return { l: this.padL, r: this.ivW - this.padR, t: this.padT, b: this.ivH - this.padB };
  }
  public rankPop(l: string): number {
    return this.byPop.indexOf(l) + 1;
  }
  public rankVal(l: string): number {
    return this.byVal.indexOf(l) + 1;
  }
  public get ivSelData(): Chapter3Datum {
    return this.ivData[this.ivSel];
  }
  public ivSelect(l: string, fromInput: boolean = false): void {
    if (!this.ivData[l]) return;
    this.ivSel = l;
    if (!fromInput) this.ivInputValue = l;
  }
  public ivInput(raw: string): void {
    const v = (raw || '').toUpperCase().replace(/[^A-Z]/g, '');
    this.ivInputValue = v;
    if (v && this.ivData[v]) this.ivSelect(v, true);
  }
}
